import React, { useState, useEffect, useRef, useContext } from "react";
import { v4 } from "uuid";
import { TextField, PrimaryButton, DefaultButton } from "@fluentui/react";
import { css } from "@emotion/css";

import { config } from "../../config";
import SLSearchAliasInput, { ISLSearchAliasInputRef } from "./SLSearchAliasInput";
import { AuthorizationToken } from "../../services/RequestHelpers";
import { SubledgerEndpoints } from "../../SubledgerEndpoints";
import { ISLEntryGridItem } from "../../schemas/SLEntryGridSchemas";
import SLSearchFiscalPeriods, { ISLSearchFiscalPeriodsRef } from "./SLSearchFiscalPeriods";
import SLSearchDocumentTypeAutocomplete, {
  ISLSearchDocumentTypeAutocompleteRef
} from "./SLSearchDocumentTypeAutocomplete";
import SLSearchCompanyCodesAutocomplete, {
  ISLSearchCompanyCodesAutocompleteRef
} from "./SLSearchCompanyCodesAutocomplete";
import SLSearchFiscalYearAutocomplete, { ISLSearchFiscalYearAutocompleteRef } from "./SLSearchFiscalYearAutocomplete";
import SLSearchDocumentStatusIdAutocomplete, {
  ISLSearchDocumentStatusIdAutocompleteRef
} from "./SLSearchDocumentStatusIdAutocomplete";
import { ISearchState, useSearchStateFromLocation } from "./useSearchStateFromLocation";
import { SubledgerContext } from "../../contexts/SubledgerContext";
import { UserContext, IUserProviderState } from "../../contexts/UserContext";
import { SLLoadingStatus, leftPadDocumentNumber } from "../../utilities/utilities";

const id = v4();

const toNumberArray = (arr: string[]) => arr.map((i) => Number(i)).filter((i) => !isNaN(i));

async function getGridItemsFromPayload(
  getTokenFn: IUserProviderState["accessToken"],
  {
    documentStatus,
    documentTypes,
    fiscalYears,
    documentNumber,
    companyCodes,
    aliasType,
    aliases,
    fiscalPeriods
  }: ISearchState
) {
  const StatusIds: number[] = documentStatus;
  const DocumentType: string[] = documentTypes;
  const FiscalYear: number[] = toNumberArray(fiscalYears);
  const DocumentNumber: string = documentNumber;
  const CompanyCodes: number[] = toNumberArray(companyCodes);
  const SearchBy: string = aliasType;
  const Aliases: string[] = aliases;
  const FiscalPeriod: number[] = fiscalPeriods;

  const data = {
    StatusIds,
    DocumentType,
    FiscalYear,
    DocumentNumber,
    CompanyCodes,
    SearchBy,
    Aliases,
    FiscalPeriod
  };
  try {
    const url = config.get("subledgerUrl") + SubledgerEndpoints.search;
    const Authorization = await AuthorizationToken(getTokenFn);
    const response = await fetch(url, {
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization
      },
      body: JSON.stringify(data)
    } as any);
    const items = await response.json();
    return items;
  } catch (e) {
    // return empty for now
    console.error(e);
    return [];
  }
}

export enum SearchFormLoadingStatus {
  pending = "pending",
  loading = "loading",
  loaded = "loaded"
}

interface ISLSearchFormProps {
  searchState: ISearchState | undefined;
  setGridItems: (items: ISLEntryGridItem[], searchState: ISearchState) => void;
  setGridLoading: (value: boolean) => void;
}

const formStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr) auto;
  grid-column-gap: 24px;
  grid-row-gap: 16px;
  margin: 0 24px 16px 24px;
`;

const SLSearchForm = (props: ISLSearchFormProps): JSX.Element => {
  const { accessToken } = useContext(UserContext);
  const { companyCodes, documentTypes, fiscalYears } = useContext(SubledgerContext);
  const [companyCodesLoading, setCompanyCodesLoading] = useState(true);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(true);
  const [fiscalYearsLoading, setFiscalYearsLoading] = useState(true);

  const [loading, setIsLoading] = useState(true);

  const searchStateFromLocation = useSearchStateFromLocation();

  const [documentNumber, setDocumentNumber] = useState<ISearchState["documentNumber"]>("");

  const documentStatusRef = useRef<ISLSearchDocumentStatusIdAutocompleteRef>(null);
  const documentTypesRef = useRef<ISLSearchDocumentTypeAutocompleteRef>(null);
  const companyCodesRef = useRef<ISLSearchCompanyCodesAutocompleteRef>(null);
  const aliasesRef = useRef<ISLSearchAliasInputRef>(null);
  const fiscalPeriodsRef = useRef<ISLSearchFiscalPeriodsRef>(null);
  const fiscalYearsRef = useRef<ISLSearchFiscalYearAutocompleteRef>(null);

  const fetchGrid = async (theState: ISearchState) => {
    props.setGridLoading(true);
    const items = await getGridItemsFromPayload(accessToken, theState);
    props.setGridItems(items as any, theState);
    props.setGridLoading(false);
  };

  const submit = async (ev: any) => {
    ev.preventDefault();
    props.setGridLoading(true);
    if (
      documentStatusRef.current &&
      documentTypesRef.current &&
      companyCodesRef.current &&
      aliasesRef.current &&
      aliasesRef.current &&
      fiscalPeriodsRef.current &&
      fiscalYearsRef.current
    ) {
      const documentStatus = documentStatusRef.current.getDocumentStatus();
      const documentTypes = documentTypesRef.current.getDocumentTypes();
      const companyCodes = companyCodesRef.current.getCompanyCodes();
      const aliases = aliasesRef.current.getAliases();
      const aliasType = aliasesRef.current.getAliasType();
      const fiscalPeriods = fiscalPeriodsRef.current.getFiscalPeriods();
      const fiscalYears = fiscalYearsRef.current.getFiscalYears();
      const currentState: ISearchState = {
        documentStatus,
        documentTypes,
        fiscalYears,
        documentNumber: leftPadDocumentNumber(documentNumber),
        companyCodes,
        aliasType,
        aliases,
        fiscalPeriods
      };
      fetchGrid(currentState);
    }
  };

  const reset = () => {
    setDocumentNumber("");
    if (documentStatusRef.current) {
      documentStatusRef.current.reset();
    }
    if (documentTypesRef.current) {
      documentTypesRef.current.reset();
    }
    if (aliasesRef.current) {
      aliasesRef.current.reset();
    }
    if (companyCodesRef.current) {
      companyCodesRef.current.reset();
    }
    if (fiscalPeriodsRef.current) {
      fiscalPeriodsRef.current.reset();
    }
    if (fiscalYearsRef.current) {
      fiscalYearsRef.current.reset();
    }
  };

  useEffect(() => {
    const isEmptyObject = searchStateFromLocation && Object.keys(searchStateFromLocation as ISearchState).length === 0;
    if (searchStateFromLocation && !isEmptyObject) {
      const theState = searchStateFromLocation as ISearchState;
      if (companyCodesRef.current) {
        companyCodesRef.current.setInitialCompanyCodes(theState.companyCodes);
      }
      if (aliasesRef.current) {
        aliasesRef.current.setInitialAliases({
          aliases: theState.aliases,
          aliasType: theState.aliasType
        });
      }
      if (documentTypesRef.current) {
        documentTypesRef.current.setInitialDocumentTypes(theState.documentTypes);
      }
      if (documentStatusRef.current) {
        documentStatusRef.current.setInitialDocumentStatus(theState.documentStatus);
      }
      if (fiscalPeriodsRef.current) {
        fiscalPeriodsRef.current.setInitialFiscalPeriods(theState.fiscalPeriods);
      }
      if (fiscalYearsRef.current) {
        fiscalYearsRef.current.setInitialFiscalYears(theState.fiscalYears);
      }
      setDocumentNumber(theState.documentNumber);
      fetchGrid(theState);
    } else if (isEmptyObject) {
      if (aliasesRef.current) {
        aliasesRef.current.reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStateFromLocation]);

  useEffect(() => {
    if (companyCodes.status === SLLoadingStatus.Resolved) {
      setCompanyCodesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCodes.status]);
  useEffect(() => {
    if (documentTypes.status === SLLoadingStatus.Resolved) {
      setDocumentTypesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTypes.status]);
  useEffect(() => {
    if (fiscalYears.status === SLLoadingStatus.Resolved) {
      setFiscalYearsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYears.status]);

  useEffect(() => {
    if (!documentTypesLoading && !fiscalYearsLoading && !companyCodesLoading) {
      setIsLoading(false);
    }
  }, [documentTypesLoading, fiscalYearsLoading, companyCodesLoading]);

  return (
    <React.Fragment>
      <form id={id} action="#" className={formStyle}>
        <div>
          <SLSearchDocumentStatusIdAutocomplete ref={documentStatusRef} />
        </div>
        <div>
          <SLSearchDocumentTypeAutocomplete ref={documentTypesRef} loading={documentTypesLoading} />
        </div>
        <div>
          <SLSearchFiscalYearAutocomplete ref={fiscalYearsRef} loading={fiscalYearsLoading} />
        </div>
        <div>
          <SLSearchFiscalPeriods ref={fiscalPeriodsRef} />
        </div>
        <TextField
          value={documentNumber}
          label="Document Number"
          errorMessage=""
          onChange={(_, value) => setDocumentNumber(value || "")}
        />
        <div>
          <SLSearchCompanyCodesAutocomplete ref={companyCodesRef} loading={companyCodesLoading} />
        </div>
        <div>
          <SLSearchAliasInput ref={aliasesRef} />
        </div>

        <div
          className={css`
            grid-area: 2 / 4 / 3 / 5;
            display: flex;
            justify-content: end;
            flex-direction: row;
          `}
        >
          <PrimaryButton
            text="Search"
            allowDisabledFocus
            style={{ margin: "28px 0 0 8px" }}
            onClick={submit}
            type="submit"
            disabled={loading}
          />
          <DefaultButton text="Reset" allowDisabledFocus style={{ margin: "28px 0 0 8px" }} onClick={reset} />
        </div>
      </form>
    </React.Fragment>
  );
};

SLSearchForm.displayName = "SLSearchForm";

export default SLSearchForm;
